import Decimal from 'decimal.js';
import { groupBy, isEqual, orderBy } from 'lodash';

import {
  EntityInEstateStatus,
  EntityKind,
  EntityStage,
  IncomeTaxPaymentConfigGrantorPaymentMethod,
  IncomeTaxPaymentConfigNonGrantorPaymentMethod,
  NonTrustEntityTaxStatus,
  TaxDragConfigCalculationMethod,
  TrustTaxStatus,
} from '@/types/schema';

import { TaxDrag_DefaultsFragment } from './TaxDrag.generated';
import {
  StateTaxRates,
  TaxDragBaseMode,
  TaxDragComputedInputs,
  TaxDragEntity,
  TaxDragEntityGroup,
  TaxDragFormShape,
  TaxDragRateFormShape,
} from './TaxDrag.types';

const NIIT_RATE = new Decimal(3.8);
const RETURN_COMPOSITION_TOTAL = new Decimal(100);
// Match Platform ReturnComposition.Validate, in percentage points.
const RETURN_COMPOSITION_TOLERANCE = new Decimal('0.01');

const TAX_DRAG_OVERRIDE_ENTITY_KINDS = new Set<EntityKind>([
  EntityKind.IndividualPersonalAccount,
  EntityKind.JointPersonalAccount,
  EntityKind.RevocableTrust,
  EntityKind.IrrevocableTrust,
  EntityKind.SlatTrust,
  EntityKind.IlitTrust,
  EntityKind.GratTrust,
]);

const ELIGIBLE_PAYER_KINDS = new Set<EntityKind>([
  EntityKind.IndividualPersonalAccount,
  EntityKind.JointPersonalAccount,
  EntityKind.RevocableTrust,
]);

const TAX_PAYER_KIND_ORDER: Partial<Record<EntityKind, number>> = {
  [EntityKind.RevocableTrust]: 0,
  [EntityKind.JointPersonalAccount]: 1,
  [EntityKind.IndividualPersonalAccount]: 2,
};

export function getDefaultStateTaxRates(
  stateCode: string | null | undefined,
  defaults?: TaxDrag_DefaultsFragment
): StateTaxRates {
  const rates = defaults?.stateRates.find(
    (rate) => rate.stateCode === stateCode?.toUpperCase()
  );

  return {
    income: rates?.incomeTaxRate ?? null,
    capitalGains: rates?.longTermCapitalGainsRate ?? null,
  };
}

export function getDefaultTaxDragRate(
  stateCode?: string | null,
  defaults?: TaxDrag_DefaultsFragment
): TaxDragRateFormShape {
  const stateRates = getDefaultStateTaxRates(stateCode, defaults);

  return {
    calculationMethod: TaxDragConfigCalculationMethod.Manual,
    taxDragPercentage: new Decimal(0),
    federalIncomeTaxRate: defaults?.federalIncomeTaxRate ?? null,
    federalLongTermCapitalGainsRate:
      defaults?.federalLongTermCapitalGainsRate ?? null,
    stateIncomeTaxRate: stateRates.income,
    stateLongTermCapitalGainsRate: stateRates.capitalGains,
    niitEnabled: true,
    ordinaryIncomeReturnPercentage: new Decimal(100),
    longTermCapitalGainReturnPercentage: new Decimal(0),
    qualifiedDividendReturnPercentage: new Decimal(0),
    taxFreeIncomeReturnPercentage: new Decimal(0),
  };
}

export function getDefaultTaxDragForm(
  stateCode?: string | null,
  defaults?: TaxDrag_DefaultsFragment
): TaxDragFormShape {
  return {
    baseMode: TaxDragBaseMode.Default,
    globalRate: getDefaultTaxDragRate(stateCode, defaults),
    overrides: [],
    grantorPaymentMethod: IncomeTaxPaymentConfigGrantorPaymentMethod.Custom,
    nonGrantorPaymentMethod: IncomeTaxPaymentConfigNonGrantorPaymentMethod.Self,
    customPayerOrder: [],
  };
}

export function getComputedTaxDragInputs(
  rate: TaxDragRateFormShape
): TaxDragComputedInputs {
  return {
    federalIncomeTaxRate: rate.federalIncomeTaxRate,
    federalLongTermCapitalGainsRate: rate.federalLongTermCapitalGainsRate,
    stateIncomeTaxRate: rate.stateIncomeTaxRate,
    stateLongTermCapitalGainsRate: rate.stateLongTermCapitalGainsRate,
    niitEnabled: rate.niitEnabled,
    ordinaryIncomeReturnPercentage: rate.ordinaryIncomeReturnPercentage,
    longTermCapitalGainReturnPercentage:
      rate.longTermCapitalGainReturnPercentage,
    qualifiedDividendReturnPercentage: rate.qualifiedDividendReturnPercentage,
    taxFreeIncomeReturnPercentage: rate.taxFreeIncomeReturnPercentage,
  };
}

export function getReturnCompositionTotal(
  rate: TaxDragComputedInputs
): Decimal | null {
  const {
    ordinaryIncomeReturnPercentage,
    longTermCapitalGainReturnPercentage,
    qualifiedDividendReturnPercentage,
    taxFreeIncomeReturnPercentage,
  } = rate;
  if (
    ordinaryIncomeReturnPercentage == null ||
    longTermCapitalGainReturnPercentage == null ||
    qualifiedDividendReturnPercentage == null ||
    taxFreeIncomeReturnPercentage == null
  )
    return null;
  return ordinaryIncomeReturnPercentage
    .plus(longTermCapitalGainReturnPercentage)
    .plus(qualifiedDividendReturnPercentage)
    .plus(taxFreeIncomeReturnPercentage);
}

export function isValidReturnComposition(rate: TaxDragComputedInputs): boolean {
  const total = getReturnCompositionTotal(rate);
  return (
    total != null &&
    total
      .minus(RETURN_COMPOSITION_TOTAL)
      .abs()
      .lte(RETURN_COMPOSITION_TOLERANCE)
  );
}

export function calculateEffectiveTaxDrag(
  rate: TaxDragComputedInputs
): Decimal | null {
  const {
    federalIncomeTaxRate,
    stateIncomeTaxRate,
    federalLongTermCapitalGainsRate,
    stateLongTermCapitalGainsRate,
    ordinaryIncomeReturnPercentage,
    longTermCapitalGainReturnPercentage,
    qualifiedDividendReturnPercentage,
  } = rate;
  if (
    federalIncomeTaxRate == null ||
    stateIncomeTaxRate == null ||
    federalLongTermCapitalGainsRate == null ||
    stateLongTermCapitalGainsRate == null ||
    ordinaryIncomeReturnPercentage == null ||
    longTermCapitalGainReturnPercentage == null ||
    qualifiedDividendReturnPercentage == null ||
    rate.taxFreeIncomeReturnPercentage == null
  )
    return null;
  const niit = rate.niitEnabled ? NIIT_RATE : new Decimal(0);
  const ordinaryRate = federalIncomeTaxRate.plus(stateIncomeTaxRate).plus(niit);
  const capitalGainsRate = federalLongTermCapitalGainsRate
    .plus(stateLongTermCapitalGainsRate)
    .plus(niit);

  return ordinaryIncomeReturnPercentage
    .times(ordinaryRate)
    .plus(longTermCapitalGainReturnPercentage.times(capitalGainsRate))
    .plus(qualifiedDividendReturnPercentage.times(capitalGainsRate))
    .dividedBy(100);
}

export function getDisplayedTaxDragRate(
  rate: TaxDragRateFormShape
): Decimal | null {
  if (rate.calculationMethod === TaxDragConfigCalculationMethod.Computed) {
    if (
      rate.savedComputedRate &&
      isEqual(rate.savedComputedRate.inputs, getComputedTaxDragInputs(rate))
    ) {
      return rate.savedComputedRate.percentage;
    }
    return calculateEffectiveTaxDrag(rate);
  }
  return rate.taxDragPercentage;
}

export function groupTaxDragEntities(
  entities: TaxDragEntity[]
): Partial<Record<TaxDragEntityGroup, TaxDragEntity[]>> {
  return groupBy(
    entities.filter(
      (entity) =>
        TAX_DRAG_OVERRIDE_ENTITY_KINDS.has(entity.kind) &&
        (entity.kind !== EntityKind.GratTrust ||
          entity.stage === EntityStage.Active)
    ),
    (entity): TaxDragEntityGroup => {
      if (entity.stage === EntityStage.Draft) {
        return TaxDragEntityGroup.Draft;
      }
      if (entity.inEstateStatus === EntityInEstateStatus.InEstate) {
        return TaxDragEntityGroup.InEstate;
      }
      return TaxDragEntityGroup.OutOfEstate;
    }
  );
}

export function isEligibleTaxPayer(entity: TaxDragEntity): boolean {
  if (entity.inEstateStatus !== EntityInEstateStatus.InEstate) return false;
  if (entity.stateInEstateStatus !== EntityInEstateStatus.InEstate)
    return false;
  if (!ELIGIBLE_PAYER_KINDS.has(entity.kind)) return false;

  if (entity.kind === EntityKind.RevocableTrust) {
    return entity.taxStatus === TrustTaxStatus.GrantorTrust;
  }
  return entity.taxStatus === NonTrustEntityTaxStatus.Taxable;
}

export function getTaxPayerOptions(entities: TaxDragEntity[]): TaxDragEntity[] {
  return entities.filter(isEligibleTaxPayer).sort((left, right) => {
    // Eligibility limits both kinds to keys in TAX_PAYER_KIND_ORDER.
    const kindDifference =
      TAX_PAYER_KIND_ORDER[left.kind]! - TAX_PAYER_KIND_ORDER[right.kind]!;
    if (kindDifference !== 0) return kindDifference;

    const valueDifference = right.currentValue.comparedTo(left.currentValue);
    if (valueDifference !== 0) return valueDifference;

    const nameDifference = left.displayName.localeCompare(right.displayName);
    if (nameDifference !== 0) return nameDifference;

    return left.id.localeCompare(right.id);
  });
}

export function applySavedTaxPayerOrder(
  entities: TaxDragEntity[],
  savedOrder: string[]
): TaxDragEntity[] {
  const options = getTaxPayerOptions(entities);
  const savedIndexById = new Map(savedOrder.map((id, index) => [id, index]));

  return orderBy(
    options,
    [
      (entity) => savedIndexById.get(entity.id) ?? Number.MAX_SAFE_INTEGER,
      (entity) => options.findIndex(({ id }) => id === entity.id),
    ],
    ['asc', 'asc']
  );
}
